import { useState, useEffect } from 'react';
import { fetchWithCache } from '../../utils/apiCache';

export function useTableData({ targetTable, token, apiPrefix, uiConfig, onMissingConfig, injectedColumns, preferencesLoaded, baseFilters, activeSubgrids, searchQuery, limit, offset, distinctMode, hiddenColumns }) {
    const schemaPrefix = apiPrefix && apiPrefix.startsWith('/api/remote-sql') ? apiPrefix : '/api/data';

    const [schema, table] = targetTable && targetTable.includes('.') 
        ? targetTable.split('.') 
        : [null, targetTable];

    const [data, setData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [columns, setColumns] = useState([]);
    const [fks, setFks] = useState([]);
    const [reverseFks, setReverseFks] = useState([]);
    const [fkOptions, setFkOptions] = useState({});
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [schemaWarning, setSchemaWarning] = useState(null);
    
    const [tablesList, setTablesList] = useState([]);
    const [polymorphicLabels, setPolymorphicLabels] = useState({});
    const [dependencyGraph, setDependencyGraph] = useState(null);
    const [deletingLoading, setDeletingLoading] = useState(false);
    const [lastQuery, setLastQuery] = useState('');

    const [isPolymorphicTable, setIsPolymorphicTable] = useState(false);

    useEffect(() => {
        setData([]);
        setColumns([]);
        setTotalCount(0);
        setError(null);
    }, [targetTable, distinctMode]);

    useEffect(() => {
        if (schema && table && preferencesLoaded) {
            loadTableContext();
        }
    }, [targetTable, token, apiPrefix, preferencesLoaded, JSON.stringify(injectedColumns), searchQuery, limit, offset, distinctMode, JSON.stringify(baseFilters), JSON.stringify(activeSubgrids), JSON.stringify(hiddenColumns)]);

    const loadTableContext = async () => {
        setLoading(true);
        setError(null);
        setSchemaWarning(null);
        setPolymorphicLabels({});

        try {
            const headers = { 'Authorization': `Bearer ${token}` };
            const requestBody = { 
                columns: injectedColumns || [],
                hiddenColumns: hiddenColumns || {},
                baseFilters: baseFilters || [],
                countSubgrids: (activeSubgrids || []).filter(sg => !sg.endsWith(':SELF')),
                searchQuery: searchQuery || '',
                limit: limit || 100,
                offset: offset || 0,
                distinctMode: distinctMode || false
            }; 

            const [dataRes, metaRes, fksRes, tablesRes] = await Promise.all([
                fetch(`${apiPrefix}/${schema}/${table}/query`, { 
                    method: 'POST', 
                    headers: { ...headers, 'Content-Type': 'application/json' },
                    body: JSON.stringify(requestBody)
                }),
                fetchWithCache(`${schemaPrefix}/schema/metadata/${schema}/${table}`, { headers }),
                fetchWithCache(`${schemaPrefix}/schema/fks`, { headers }),
                fetchWithCache(`${schemaPrefix}/schema/tables`, { headers })
            ]);

            if (!dataRes.ok) {
                const errBody = await dataRes.json().catch(() => ({}));
                setLastQuery(errBody.query || 'N/A');
                throw new Error(`Data query failed: ${errBody.error || dataRes.statusText}. Query: ${errBody.query || 'N/A'}`);
            }
            if (!metaRes.ok) throw new Error(`Failed to load table metadata: ${metaRes.status} ${metaRes.statusText}`);

            const tableData = await dataRes.json();
            setLastQuery(tableData.debugQuery || 'Query hidden or not provided by server');
            const tableMeta = await metaRes.json();
            const allFks = fksRes.ok ? await fksRes.json() : [];
            const schemaTables = tablesRes.ok ? await tablesRes.json() : [];

            const tableFullName = `${schema}.${table}`;
            const relevantFks = Array.isArray(allFks) ? allFks.filter(fk => fk.TableName === tableFullName) : [];
            const related1toM = Array.isArray(allFks) ? allFks.filter(fk => fk.RefTableName === tableFullName) : [];

            setTablesList(Array.isArray(schemaTables) ? schemaTables.map(t => t.TableName) : []);
            
            const rawData = Array.isArray(tableData.data) ? tableData.data : (Array.isArray(tableData) ? tableData : []);
            setData(rawData);
            setTotalCount(tableData.totalCount !== undefined ? tableData.totalCount : rawData.length);
            // Augment metadata columns with injected ones
            let augmentedColumns = Array.isArray(tableMeta) ? [...tableMeta] : [];
            if (Array.isArray(injectedColumns) && injectedColumns.length > 0) {
                injectedColumns.forEach(inj => {
                    augmentedColumns.push({
                        ColumnName: inj,
                        DataType: 'nvarchar', // Dynamic columns treated as string typically
                        IsPrimaryKey: false,
                        IsIdentity: false
                    });
                });
            }
            setColumns(augmentedColumns);
            setFks(relevantFks);
            setReverseFks(related1toM);

            const polyDefsRes = await fetchWithCache(`${schemaPrefix}/schema/polymorphic/${schema}/${table}`, { headers });
            const polyDefs = polyDefsRes.ok ? await polyDefsRes.json() : [];
            const hasPoly = polyDefs && polyDefs.length > 0;
            setIsPolymorphicTable(hasPoly);

            await loadDropdownOptions(relevantFks, headers);
            if (hasPoly) {
                await loadPolymorphicLabels(rawData, headers);
            }
            validateSchema(rawData, tableMeta);

        } catch (err) {
            console.error("loadTableContext Error:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const validateSchema = (currentData, meta) => {
        if (!Array.isArray(meta)) return;
        const pkCols = meta.filter(c => c.IsPrimaryKey);
        if (pkCols.length === 0) {
            setSchemaWarning("Warning: This table lacks a Primary Key. Editing and Deleting are disabled.");
            return;
        }
        if (pkCols.length > 1) {
            // Composite key detected - no warning needed, it's fully supported now.
        }

        const dataArray = Array.isArray(currentData) ? currentData : [];
        const uniqueKeys = new Set();
        let hasDuplicates = false;
        
        for (const row of dataArray) {
            const keyStr = pkCols.map(c => row[c.ColumnName]).join('|');
            if (uniqueKeys.has(keyStr)) {
                hasDuplicates = true;
                break;
            }
            uniqueKeys.add(keyStr);
        }

        if (hasDuplicates) {
            setSchemaWarning(`CRITICAL SCHEMA ERROR: The designated Primary Key contains duplicate values. Editing disabled to prevent multi-row corruption.`);
        }
    };

    const loadDropdownOptions = async (relevantFks, headers) => {
        const optionsMap = {};
        let triggeredMissingConfig = false;

        for (const fk of relevantFks) {
            const [refSchema, refTable] = fk.RefTableName.split('.');
            try {
                const res = await fetchWithCache(`${schemaPrefix}/${refSchema}/${refTable}`, { headers });
                if (!res.ok) continue;
                
                const text = await res.text();
                let refData = [];
                try { refData = JSON.parse(text); } catch (e) { continue; }
                
                const targetUiConf = Array.isArray(uiConfig) ? uiConfig.find(c => c.TargetSchema === refSchema && c.TargetTable === refTable) : null;
                
                if (!targetUiConf && onMissingConfig && !triggeredMissingConfig) {
                    triggeredMissingConfig = true;
                    onMissingConfig({ schema: refSchema, table: refTable });
                }

                const displayCol = targetUiConf ? targetUiConf.DisplayColumn : fk.RefColumnName;

                optionsMap[fk.ColumnName] = {
                    displayCol,
                    options: Array.isArray(refData) ? refData.map(row => ({
                        value: row[fk.RefColumnName],
                        label: row[displayCol] || row[fk.RefColumnName]
                    })) : [],
                    fullPath: `${refSchema}.${refTable}.${displayCol}`
                };
            } catch (err) {
                console.error(`Failed to load options for FK ${fk.ColumnName}:`, err);
            }
        }
        setFkOptions(optionsMap);
    };

    const loadPolymorphicLabels = async (currentData, headers) => {
        try {
            const polyDefsRes = await fetch(`${schemaPrefix}/schema/polymorphic/${schema}/${table}`, { headers });
            if (!polyDefsRes.ok) return;
            const polyDefs = await polyDefsRes.json();
            
            if (!polyDefs || polyDefs.length === 0) return;

            const labelsMap = {};
            
            for (const def of polyDefs) {
                const pSchema = def.TargetSchema;
                const pTable = def.TargetTable;
                
                try {
                    const res = await fetchWithCache(`${schemaPrefix}/${pSchema}/${pTable}`, { headers });
                    if (!res.ok) continue;
                    const pData = await res.json();

                    const targetUiConf = Array.isArray(uiConfig) ? uiConfig.find(c => c.TargetSchema === pSchema && c.TargetTable === pTable) : null;
                    const displayCol = targetUiConf ? targetUiConf.DisplayColumn : null;

                    const pkCol = def.TargetIDColumn;

                    if (Array.isArray(pData)) {
                        pData.forEach(pRow => {
                            const key = `${pSchema}.${pTable}.${pRow[pkCol]}`;
                            labelsMap[key] = displayCol && pRow[displayCol] ? pRow[displayCol] : pRow[pkCol];
                        });
                    }
                } catch (err) {
                    console.error(`Failed to load polymorphic labels for ${pSchema}.${pTable}:`, err);
                }
            }
            setPolymorphicLabels(labelsMap);
        } catch (err) {
            console.error("Failed to load polymorphic definitions:", err);
        }
    };

    const checkDependencies = async (pkMap) => {
        setDependencyGraph(null);
        setDeletingLoading(true);

        try {
            const keysStr = JSON.stringify(pkMap);
            const res = await fetch(`${schemaPrefix}/dependencies/${schema}/${table}?keys=${encodeURIComponent(keysStr)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({ error: "Unknown dependency check failure." }));
                throw new Error(errData.error || `HTTP ${res.status}: Failed to execute check.`);
            }
            const graph = await res.json();
            setDependencyGraph(graph);
            return graph;
        } catch (err) {
            alert(`Dependency Check Failed: ${err.message}`);
            return null;
        } finally {
            setDeletingLoading(false);
        }
    };

    const deleteRow = async (pkMap) => {
        try {
            const keysStr = JSON.stringify(pkMap);
            const res = await fetch(`${schemaPrefix}/${schema}/${table}/row?keys=${encodeURIComponent(keysStr)}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok) {
                const errData = await res.json();
                throw new Error(errData.error || "Deletion command rejected by server.");
            }

            setData(prev => prev.filter(row => {
                // If every key in pkMap matches the row, filter it out
                return !Object.entries(pkMap).every(([col, val]) => row[col] === val);
            }));
            setDependencyGraph(null);
            return true;
        } catch (err) {
            alert(`Deletion Error: ${err.message}`);
            return false;
        }
    };

    const saveRow = async (isUpdate, pkMap, payload) => {
        try {
            const url = isUpdate 
                ? `${schemaPrefix}/${schema}/${table}/row`
                : `${schemaPrefix}/${schema}/${table}`;
                
            const body = isUpdate ? JSON.stringify({ keys: pkMap, data: payload }) : JSON.stringify(payload);

            const res = await fetch(url, {
                method: isUpdate ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: body
            });

            if (!res.ok) {
                const errData = await res.json();
                throw new Error(errData.error || "Save rejected.");
            }

            const responseData = await res.json();

            if (isUpdate) {
                setData(prev => prev.map(row => {
                    const isMatch = Object.entries(pkMap).every(([col, val]) => row[col] === val);
                    return isMatch ? { ...row, ...payload } : row;
                }));
            } else {
                setData(prev => [responseData, ...prev]);
            }
            return true;
        } catch (err) {
            alert(`Save Error: ${err.message}`);
            return false;
        }
    };

    const getPrimaryKeyColumns = () => columns.filter(c => c.IsPrimaryKey);

    return {
        schema,
        table,
        data,
        totalCount,
        columns,
        fks,
        fkOptions,
        loading,
        error,
        schemaWarning,
        tablesList,
        polymorphicLabels,
        reverseFks,
        dependencyGraph,
        deletingLoading,
        isPolymorphicTable,
        getPrimaryKeyColumns,
        checkDependencies,
        deleteRow,
        saveRow,
        setPolymorphicLabels,
        setDependencyGraph,
        lastQuery
    };
}
